import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";

// Update the theme selection part of your component
const ThemeSelector = ({
  type,
  selectedTheme,
  handleThemeChange,
  isDark,
  showSnackbar,
}) => {
  const [customMode, setCustomMode] = useState(false);
  const [customTheme, setCustomTheme] = useState("");
  const inputRef = useRef(null);

  // Handle switching to custom input mode
  const enableCustomMode = () => {
    setCustomMode(true);
    setCustomTheme("");
    // Focus will be set by useEffect
  };

  // Handle switching back to select mode
  const confirmCustomTheme = () => {
    if (customTheme.trim()) {
      handleThemeChange(type, customTheme.trim());
      setCustomMode(false);
    } else {
      // Show snackbar when trying to confirm an empty theme
      showSnackbar("Skriv in temat och bekräfta.", "info");
    }
  };

  // Handle custom theme input change
  const handleCustomInputChange = (e) => {
    setCustomTheme(e.target.value);
  };

  // Handle pressing Enter in the text field
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      confirmCustomTheme();
    }
  };

  // Handle blur event (clicking outside)
  const handleBlur = () => {
    if (customMode) {
      confirmCustomTheme();
    }
  };

  // Set focus on the text field when entering custom mode
  useEffect(() => {
    if (customMode && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 50);
    }
  }, [customMode]);

  // Determine if the current selection is a custom theme
  const isCustomTheme =
    selectedTheme !== "AI får välja" &&
    !getStandardThemes(type).some((item) => item.value === selectedTheme);

  return (
    <FormControl sx={{ mb: 2, mt: 0.5 }} fullWidth>
      <InputLabel id={`${type}-theme-label`} sx={{ fontWeight: "500" }}>
        Tema
      </InputLabel>

      {customMode ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            ref={inputRef}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Skriv in valfritt tema"
            value={customTheme}
            onChange={handleCustomInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
            maxLength="2000"
            sx={{
              backgroundColor: isDark ? "#1c262f" : "white",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
            InputProps={{
              style: {
                fontWeight: "500",
                fontSize: "0.9rem",
              },
            }}
          />
          <Tooltip title="Bekräfta">
            <IconButton onClick={confirmCustomTheme} sx={{ ml: 1 }}>
              <DoneIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Select
          labelId={`${type}-theme-label`}
          value={selectedTheme}
          onChange={(e) => handleThemeChange(type, e.target.value)}
          label={`Tema ${type}`}
          size="small"
          sx={{
            backgroundColor: isDark ? "#1c262f" : "white",
            fontSize: "0.9rem", // This will style the selected item text
            fontWeight: "500",

            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            "& .MuiMenuItem-root": {
              // This targets all MenuItem components within the Select
              fontSize: "0.9rem", // Set your desired font size here
              fontWeight: "500",
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: "1rem",
              fontWeight: "500",
            },
          }}
          MenuProps={{
            sx: {
              "& .MuiMenuItem-root": {
                fontSize: "0.9rem",
                fontWeight: "500",
              },
            },
          }}
        >
          <MenuItem value="AI får välja">AI får välja</MenuItem>

          {/* Add the custom theme option */}
          <MenuItem value="__custom__" onClick={enableCustomMode}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Skriv in själv
            </Box>
          </MenuItem>

          {/* If there's a custom theme already selected, show it first */}
          {isCustomTheme && (
            <MenuItem value={selectedTheme}>{selectedTheme}</MenuItem>
          )}

          {/* Standard themes based on question type */}
          {getStandardThemes(type).map((theme) => (
            <MenuItem key={theme.value} value={theme.value}>
              {theme.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  );
};

// Helper function to get standard themes based on question type
const getStandardThemes = (type) => {
  if (type === "XYZ") {
    return [
      { value: "Algebra", label: "Algebra" },
      { value: "Geometri", label: "Geometri" },
      { value: "Aritmetik", label: "Aritmetik" },
      { value: "Kalkyl", label: "Kalkyl" },
      { value: "Trigonometri", label: "Trigonometri" },
      { value: "Statistik", label: "Statistik" },
      { value: "Sannolikhetslära", label: "Sannolikhetslära" },
      { value: "Talteori", label: "Talteori" },
      { value: "Kombinatorik", label: "Kombinatorik" },
      { value: "Linjär algebra", label: "Linjär algebra" },
    ];
  } else if (type === "DTK") {
    return [
      { value: "Algebra", label: "Algebra" },
      { value: "Geometri", label: "Geometri" },
      { value: "Aritmetik", label: "Aritmetik" },
      { value: "Kalkyl", label: "Kalkyl" },
      { value: "Trigonometri", label: "Trigonometri" },
    ];
  } else {
    return [
      { value: "Samhällskunskap", label: "Samhällskunskap" },
      { value: "Politik", label: "Politik" },
      { value: "Historia", label: "Historia" },
      { value: "Religionskunskap", label: "Religionskunskap" },
      { value: "Geografi", label: "Geografi" },
      { value: "Naturvetenskap", label: "Naturvetenskap" },
      { value: "Teknik", label: "Teknik" },
      { value: "Miljö", label: "Miljö" },
      { value: "Filosofi", label: "Filosofi" },
      { value: "Psykologi", label: "Psykologi" },
      { value: "Litteratur", label: "Litteratur" },
      { value: "Konst", label: "Konst" },
      { value: "Musik", label: "Musik" },
      { value: "Språk", label: "Språk" },
      { value: "Ekonomi", label: "Ekonomi" },
      { value: "Finans", label: "Finans" },
      { value: "Juridik", label: "Juridik" },
      { value: "Medicin", label: "Medicin" },
      { value: "Utbildning", label: "Utbildning" },
    ];
  }
};

export default ThemeSelector;
